interface ToastPayload {
  variant: "default" | "destructive" | "success" | "alert";
  description: string;
  duration: number;
}

const DEFAULT_DURATION: number = 3000;

export const DASHBOARD_TOASTS = {
  EXPORT_REQUESTED: (label: string): ToastPayload => ({
    variant: "default",
    description: `Exportando ${label}`,
    duration: DEFAULT_DURATION,
  }),
  DOWNLOAD_REQUESTED: (label: string): ToastPayload => ({
    variant: "default",
    description: `Baixando ${label}`,
    duration: DEFAULT_DURATION,
  }),
  EXPORT_EMPTY: (label: string): ToastPayload => ({
    variant: "default",
    description: `Nenhum registro para exportar em ${label}`,
    duration: DEFAULT_DURATION,
  }),
  EXPORT_FAILED: (reason: string): ToastPayload => ({
    variant: "destructive",
    description: `Não foi possível exportar: ${reason}`,
    duration: DEFAULT_DURATION,
  }),
  DOWNLOAD_DIVERGED: (label: string): ToastPayload => ({
    variant: "alert",
    description: `${label} foi regerada a partir dos dados de hoje e não confere mais com o arquivo enviado — o ciclo mudou desde o envio.`,
    duration: DEFAULT_DURATION,
  }),
  DOWNLOAD_FAILED: (reason: string): ToastPayload => ({
    variant: "destructive",
    description: `Não foi possível baixar: ${reason}`,
    duration: DEFAULT_DURATION,
  }),
  RESEND_FAILED: (reason: string): ToastPayload => ({
    variant: "destructive",
    description: `Não foi possível reenviar: ${reason}`,
    duration: DEFAULT_DURATION,
  }),
  RESENT: (): ToastPayload => ({
    variant: "default",
    description: "Reenviado com sucesso (motivo registrado)",
    duration: DEFAULT_DURATION,
  }),
} as const;

export const DASHBOARD_LABELS = {
  NOT_SENT_YET: "Gerado, aguardando envio",
  PAGE_TITLE: "Dashboard",
  PAGE_DESCRIPTION:
    "Visão consolidada da operação para o Admin Conciliação. Itens pendentes mostrados são os que aguardam sua ação.",
  EXPORT_MENU: "Exportar Tabelas",
  EXPORT_FALLBACK: "erro inesperado ao gerar a planilha",
  RESEND_FALLBACK: "erro inesperado no reenvio",
  DOWNLOAD_FALLBACK: "erro inesperado ao regerar a planilha",
  CYCLE_OPEN: "Ciclo aberto",
  DAYS_TO_CLOSE: "Dias para fechar",
  PROCESSED_IN_CYCLE: "Processados no ciclo",
  VS_PREVIOUS_CYCLE: "vs ciclo anterior",
  CASES: (count: number): string => `${count} casos`,
  APPROVED_PERCENTAGE: (percentage: number): string =>
    `${percentage}% aprovados`,
  CASES_DIFF: (diff: number): string => `${diff >= 0 ? "+" : ""}${diff} casos`,
  PREVIOUS_BASELINE: (previous: number): string => `(${previous} ant.)`,
  DAYS: (days: number): string => `${days} dias`,
  COMPARISON_TOOLTIP: (interval: string): string =>
    `Comparativo com o ciclo fiscal anterior (${interval})`,
  CARD_LOSSES: "Perdas",
  CARD_PAYROLL_DISCOUNT: "Desconto Folha",
  CARD_REVERSALS: "Reversões",
  CARD_HR_APPROVAL: "Aprovação RH",
  PAYROLL_DISCOUNT_TOOLTIP:
    "Vales aprovados pelo RH que viram lançamento de desconto em folha no Sênior",
  LOSSES_DELTA_TOOLTIP:
    "Queda de perdas é resultado positivo, por isso a variação negativa aparece em verde",
  RESOLVED: "Resolvidas",
  RESOLVED_MALE: "Resolvidos",
  PENDING: "Pendentes",
  ANSWERED: "Respondidos",
  WAITING: "Aguardando",
  REVERSAL_PAYROLL_DISCOUNT: "Desconto em Folha",
  REVERSAL_LOSS: "Contabiliz. de Perdas",
  REVERSAL_PERIOD_AVERAGE: "Média do Período",
  REVERSAL_AVERAGE_TOOLTIP: (cycles: number): string =>
    `Média do total revertido nos ${cycles} ciclos anteriores — serve para comparar o ciclo corrente com o histórico`,
  PENDING_ACTIONS_TITLE: "Itens Pendentes de Ação",
  PENDING_LOSSES: "perdas pendentes de resolução",
  WAITING_HR: "vales aguardando resposta do RH",
  REJECTED_BY_HR: "vales reprovados pelo RH",
  SEE_ITEMS: "Ver itens",
  HISTORY_TITLE: "Histórico de Envios",
  HISTORY_HINT: "Clique em uma linha para expandir por destinatário.",
  HISTORY_WINDOW: (days: number): string => `Últimos ${days} dias`,
  RECIPIENTS_SUMMARY: (delivered: number, total: number): string =>
    `${total} destinatários · ✓ ${delivered}/${total} entregues`,
  RECIPIENTS_SUMMARY_WITH_FAILURES: (
    delivered: number,
    total: number,
    failed: number,
  ): string =>
    `${total} destinatários · ${delivered}/${total} entregues · ${failed} falha(s)`,
  STATUS_DELIVERED: "Entregue",
  STATUS_FAILED: "Falhou",
  STATUS_HARD_BOUNCE: "Endereço inválido",
  SHIPPING_OK: "Entregue",
  SHIPPING_ATTENTION: "Atenção",
  DOWNLOAD: "Baixar",
  RESEND: "Reenviar",
  RESEND_TITLE: (kindLabel: string): string => `Reenviar ${kindLabel}`,
  RESEND_REASON: "Motivo do reenvio (opcional)",
  RESEND_AUDIT_NOTE: "O reenvio fica registrado no histórico (auditoria).",
  RESEND_CONFIRM: "Reenviar agora",
  RESEND_BLOCKED_TOOLTIP:
    "Endereço rejeitou a entrega em definitivo. Atualize o cadastro em E-mails de Setores antes de reenviar.",
  LAST_FAILURE_AT: (moment: string): string => `Última falha em ${moment}`,
  CANCEL: "Cancelar",
  READ_CONFIRMED: "Leitura confirmada",
  READ_NOT_CONFIRMED: "Leitura não confirmada",
  READ_CONFIRMED_TOOLTIP: (moment: string): string =>
    `Abertura registrada em ${moment}`,
  READ_NOT_CONFIRMED_TOOLTIP:
    "Não confirmamos a leitura. O registro depende do cliente de e-mail carregar imagens, então ausência de confirmação não significa que o destinatário não leu.",
  CRON_FAILURE_TITLE: "Falha na geração automática das 23h",
  CRON_FAILURE_DESCRIPTION:
    "O snapshot do ciclo não foi gerado no horário previsto. As planilhas do dia 1 podem sair incompletas.",
  PREVIOUS_PAGE: "Página anterior",
  NEXT_PAGE: "Próxima página",
  HISTORY_EMPTY: "Nenhum envio no período selecionado",
} as const;
